import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ChevronLeft, ChevronRight, ZoomIn, X } from 'lucide-react';
import Seo from '../components/Seo';
import { CaminoThanks } from '../components/CaminoThanks';
import { CaminoDownloadGate } from '../components/CaminoDownloadGate';
import { CAMINO_PAGES } from '../config/caminoPages';

export default function CaminoInfografiaPage() {
  const { slug } = useParams();
  const [zoom, setZoom] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const idx = CAMINO_PAGES.findIndex((p) => p.slug === slug);
  if (idx === -1) return <Navigate to="/" replace />;
  const cfg = CAMINO_PAGES[idx];
  const prev = CAMINO_PAGES[(idx - 1 + CAMINO_PAGES.length) % CAMINO_PAGES.length];
  const next = CAMINO_PAGES[(idx + 1) % CAMINO_PAGES.length];

  return (
    <div
      className="min-h-screen flex flex-col text-[#2F2F2F]"
      style={{
        background: `linear-gradient(180deg, ${cfg.accent}22 0%, #ffffff 40%, #ffffff 100%)`,
      }}
    >
      <Seo
        path={`/camino/${cfg.slug}`}
        title={cfg.title}
        description={cfg.seoDescription}
        image={cfg.image}
      />

      {/* Top greeting */}
      <header className="px-5 pt-8 pb-4 text-center">
        <Link to="/" className="inline-block text-xs font-bold tracking-widest text-gray-500 hover:text-[#4A4A4A]" data-testid="camino-home-link">
          SPANISCHENLARED.COM
        </Link>
        <h1
          className="mt-3 text-2xl sm:text-3xl font-extrabold leading-tight"
          data-testid={`camino-greeting-${cfg.slug}`}
        >
          {cfg.greeting}
        </h1>
        <p className="mt-1.5 text-sm text-gray-600">{cfg.subline}</p>
      </header>

      {/* Infographic container — fills width, tap to zoom */}
      <main className="flex-1 px-3 sm:px-6">
        <motion.button
          type="button"
          onClick={() => setZoom(true)}
          data-testid={`camino-image-${cfg.slug}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="relative block w-full max-w-2xl mx-auto rounded-2xl overflow-hidden bg-white shadow-lg border border-black/5 focus:outline-none focus:ring-4 focus:ring-[#7DBDD9]/40 group"
          aria-label="Ampliar infografía"
        >
          <img
            src={cfg.image}
            alt={cfg.title}
            className="w-full h-auto block select-none pointer-events-none"
            loading="eager"
            draggable={false}
          />
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 text-white text-[11px] font-semibold backdrop-blur-sm">
            <ZoomIn className="w-3.5 h-3.5" />
            Toca para ampliar
          </span>
        </motion.button>

        {/* Download (email gate) */}
        {!downloaded && (
          <CaminoDownloadGate
            cfg={cfg}
            onDownloaded={() => {
              setDownloaded(true);
              setTimeout(() => document.getElementById('camino-thanks')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
            }}
          />
        )}
        {downloaded && <CaminoThanks accent={cfg.accent} slug={cfg.slug} />}
      </main>

      {/* Bottom sticky nav */}
      <nav
        className="sticky bottom-0 inset-x-0 border-t border-black/10 bg-white/95 backdrop-blur px-3 py-3"
        data-testid={`camino-bottom-nav-${cfg.slug}`}
      >
        <div className="max-w-2xl mx-auto grid grid-cols-3 gap-2 text-center">
          <Link
            to={`/camino/${prev.slug}`}
            data-testid="camino-nav-prev"
            className="flex flex-col items-center gap-0.5 py-2 rounded-xl hover:bg-gray-50 active:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5 text-[#4A4A4A]" />
            <span className="text-[11px] font-bold text-[#4A4A4A]">Infografía {prev.number}</span>
          </Link>
          <Link
            to="/"
            data-testid="camino-nav-home"
            className="flex flex-col items-center gap-0.5 py-2 rounded-xl hover:bg-gray-50 active:bg-gray-100"
          >
            <Home className="w-5 h-5 text-[#7DBDD9]" />
            <span className="text-[11px] font-bold text-[#4A4A4A]">Inicio</span>
          </Link>
          <Link
            to={`/camino/${next.slug}`}
            data-testid="camino-nav-next"
            className="flex flex-col items-center gap-0.5 py-2 rounded-xl hover:bg-gray-50 active:bg-gray-100"
          >
            <ChevronRight className="w-5 h-5 text-[#4A4A4A]" />
            <span className="text-[11px] font-bold text-[#4A4A4A]">Infografía {next.number}</span>
          </Link>
        </div>
      </nav>

      {/* Fullscreen zoom modal — native pinch-to-zoom on image */}
      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
            onClick={() => setZoom(false)}
            data-testid={`camino-zoom-${cfg.slug}`}
          >
            <button
              onClick={() => setZoom(false)}
              aria-label="Cerrar zoom"
              data-testid="camino-zoom-close"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center backdrop-blur-sm active:scale-95"
            >
              <X className="w-6 h-6" />
            </button>
            <div
              className="w-full h-full overflow-auto touch-auto flex items-center justify-center p-2"
              onClick={(e) => e.stopPropagation()}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <img
                src={cfg.image}
                alt={cfg.title}
                className="max-w-none w-auto h-auto"
                draggable={false}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
